import React, { Component } from 'react';
import { DiagnosticResult } from '../../types/survey';
import { Card, CardContent, CardHeader, CardFooter } from '../ui/Card';
import { Button } from '../ui/Button';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, Download, RefreshCw, MessageCircle, Loader2, CloudOff, Cloud } from 'lucide-react';
interface ResultsReportProps {
  result: DiagnosticResult;
  onReset: () => void;
  isSubmitting?: boolean;
  submitError?: boolean;
}
export function ResultsReport({
  result,
  onReset,
  isSubmitting = false,
  submitError = false
}: ResultsReportProps) {
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Inicial':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'Básico':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'Intermediário':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Avançado':
        return 'text-green-600 bg-green-50 border-green-200';
      default:
        return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };
  const getIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <AlertCircle className="w-6 h-6 text-red-600" data-id="element-46" />;
      case 'warning':
        return <AlertTriangle className="w-6 h-6 text-orange-500" data-id="element-47" />;
      case 'success':
        return <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-48" />;
      default:
        return <Info className="w-6 h-6 text-blue-500" data-id="element-49" />;
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleShareWhatsApp = () => {
    let message = `📊 *Diagnóstico de Maturidade em PI*\n\n`;
    message += `🎯 *Nível de Maturidade:* ${result.level}\n\n`;
    if (result.recommendations.length > 0) {
      message += `📋 *Principais Recomendações:*\n`;
      result.recommendations.forEach(rec => {
        const emoji = rec.type === 'critical' ? '🔴' : rec.type === 'warning' ? '🟡' : rec.type === 'success' ? '🟢' : 'ℹ️';
        message += `${emoji} ${rec.title}\n`;
      });
      message += `\n`;
    }
    message += `---\n`;
    message += `_Diagnóstico realizado pela ferramenta PROFNIT/ACES_`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };
  return <div className="space-y-6 max-w-3xl mx-auto animate-in fade-in duration-700" data-id="element-50">
      {/* Status de Salvamento */}
      <div className={`flex items-center justify-center gap-2 p-3 rounded-lg text-sm font-medium ${isSubmitting ? 'bg-blue-50 text-blue-700' : submitError ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-id="element-51">
        {isSubmitting ? <>
            <Loader2 className="w-4 h-4 animate-spin" data-id="element-52" />
            Salvando suas respostas na planilha...
          </> : submitError ? <>
            <CloudOff className="w-4 h-4" data-id="element-53" />
            Não foi possível salvar na planilha. Seu resultado ainda está
            disponível abaixo.
          </> : <>
            <Cloud className="w-4 h-4" data-id="element-54" />
            Respostas salvas com sucesso na planilha de pesquisa!
          </>}
      </div>

      <Card className="border-t-4 border-t-blue-900 overflow-hidden" data-id="element-55">
        <div className="bg-blue-900 text-white p-8 text-center" data-id="element-56">
          <h2 className="text-3xl font-bold mb-2" data-id="element-57">
            Diagnóstico de Maturidade em PI
          </h2>
          <p className="text-blue-100" data-id="element-58">Relatório gerado automaticamente</p>
        </div>

        <CardContent className="pt-8 pb-8" data-id="element-59">
          <div className="flex flex-col items-center justify-center mb-10" data-id="element-60">
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2" data-id="element-61">
              Seu Nível Atual
            </span>
            <div className={`px-6 py-3 rounded-full border-2 text-xl font-bold ${getLevelColor(result.level)}`} data-id="element-62">
              {result.level}
            </div>
            <p className="mt-4 text-center text-slate-600 max-w-md mx-auto" data-id="element-63">
              Com base nas suas respostas, sua empresa apresenta um nível{' '}
              <strong data-id="element-64">{result.level}</strong> de maturidade em gestão da
              Propriedade Intelectual.
            </p>
          </div>

          <div className="space-y-6" data-id="element-65">
            <h3 className="text-xl font-bold text-slate-900 border-b border-slate-200 pb-2" data-id="element-66">
              Recomendações Personalizadas
            </h3>

            {result.recommendations.length === 0 ? <div className="text-center p-8 bg-slate-50 rounded-lg text-slate-500" data-id="element-67">
                Nenhuma recomendação crítica identificada. Continue mantendo
                suas boas práticas!
              </div> : result.recommendations.map(rec => <div key={rec.id} className="flex gap-4 p-5 rounded-lg bg-white border border-slate-200 shadow-sm" data-id="element-68">
                  <div className="flex-shrink-0 mt-1" data-id="element-69">{getIcon(rec.type)}</div>
                  <div data-id="element-70">
                    <h4 className="text-lg font-bold text-slate-900 mb-1" data-id="element-71">
                      {rec.title}
                    </h4>
                    <p className="text-slate-600 leading-relaxed" data-id="element-72">
                      {rec.content}
                    </p>
                  </div>
                </div>)}
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-4 bg-slate-50 p-6" data-id="element-73">
          <div className="flex flex-col sm:flex-row gap-4 w-full" data-id="element-74">
            <Button variant="outline" onClick={onReset} className="w-full sm:w-auto" data-id="element-75">
              <RefreshCw className="mr-2 h-4 w-4" data-id="element-76" />
              Novo Diagnóstico
            </Button>
            <Button variant="primary" onClick={handlePrint} className="w-full sm:w-auto" data-id="element-77">
              <Download className="mr-2 h-4 w-4" data-id="element-78" />
              Salvar / Imprimir
            </Button>
          </div>
          <Button onClick={handleShareWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white focus:ring-green-600" data-id="element-79">
            <MessageCircle className="mr-2 h-5 w-5" data-id="element-80" />
            Enviar Resultado pelo WhatsApp
          </Button>
        </CardFooter>
      </Card>

      <div className="text-center text-slate-400 text-sm py-4" data-id="element-81">
        <p data-id="element-82">
          © 2024 PROFNIT - Mestrado Profissional em Propriedade Intelectual
        </p>
        <p data-id="element-83">Associação Comercial e Empresarial de Santarém (ACES)</p>
      </div>
    </div>;
}